import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowLeft, Mail, KeyRound, Lock, Loader2, CheckCircle2 } from "lucide-react";

const RESEND_COOLDOWN = 60;

function strength(pw) {
  const checks = [
    pw.length >= 8,
    /[A-Z]/.test(pw),
    /[a-z]/.test(pw),
    /\d/.test(pw),
    /[^A-Za-z0-9]/.test(pw),
  ];
  const passed = checks.filter(Boolean).length;
  return { passed, checks, label: ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"][passed] };
}

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);           // 1 ident · 2 otp · 3 new pw · 4 done
  const [ident, setIdent] = useState("");
  const [masked, setMasked] = useState("");
  const [otp, setOtp] = useState("");
  const [resetTok, setResetTok] = useState("");
  const [np, setNp] = useState("");
  const [cp, setCp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cool, setCool] = useState(0);
  const s = useMemo(() => strength(np), [np]);

  useEffect(() => {
    if (cool <= 0) return;
    const t = setTimeout(() => setCool(cool - 1), 1000);
    return () => clearTimeout(t);
  }, [cool]);

  const send = async () => {
    if (!ident.trim()) { toast.error("Enter your email or Agent ID"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot", { identifier: ident.trim() });
      setMasked(data.masked_email || "");
      toast.success(data.message || "Check your inbox");
      setStep(2);
      setCool(RESEND_COOLDOWN);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send OTP");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { identifier: ident.trim(), otp });
      setResetTok(data.reset_token);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (s.passed < 5) { toast.error("Password doesn't meet all requirements"); return; }
    if (np !== cp) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { reset_token: resetTok, new_password: np });
      setStep(4);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <button onClick={() => nav("/login")}
                data-testid="back-to-login"
                className="text-xs text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={12} /> Back to Sign in
        </button>
        <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">PASSWORD RESET</div>
        <h1 className="text-3xl font-editorial mt-1 mb-6">Reset Password</h1>

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter your <b>registered email</b> or <b>Agent ID</b> and we'll send a 6-digit code.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Email / Agent ID</label>
              <div className="relative">
                <input data-testid="fp-identifier" value={ident}
                       onChange={(e) => setIdent(e.target.value)}
                       placeholder="you@agency.com or TRB-DEMO01"
                       className="w-full border border-slate-200 rounded px-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
            </div>
            <button data-testid="fp-send-btn" onClick={send} disabled={busy}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded disabled:bg-slate-300 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={14} />}
              {busy ? "Sending…" : "Send OTP"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              If an account exists with these details, we've sent a 6-digit code
              {masked ? <> to <b data-testid="fp-masked">{masked}</b></> : <>.</>}
              <br />Expires in 10 minutes.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">6-digit code</label>
              <input data-testid="fp-otp" value={otp}
                     onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     placeholder="000000" maxLength={6}
                     className="w-full border border-slate-200 rounded px-3 py-2.5 text-center tracking-[0.4em] text-xl font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
            </div>
            <button data-testid="fp-verify-btn" onClick={verify} disabled={busy || otp.length !== 6}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded disabled:bg-slate-300 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {busy ? "Verifying…" : "Verify OTP"}
            </button>
            <button data-testid="fp-resend-btn" onClick={send} disabled={cool > 0 || busy}
                    className="w-full text-sm text-emerald-700 hover:underline disabled:text-slate-400">
              {cool > 0 ? `Resend OTP in ${cool}s` : "Resend OTP"}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">New Password</label>
              <div className="relative">
                <input data-testid="fp-new-password" type="password" value={np}
                       onChange={(e) => setNp(e.target.value)}
                       className="w-full border border-slate-200 rounded px-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              </div>
              <div className="mt-2">
                <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                  <div className={`h-full transition-all ${s.passed >= 5 ? "bg-emerald-500" : s.passed >= 3 ? "bg-amber-400" : "bg-red-400"}`}
                       style={{ width: `${(s.passed / 5) * 100}%` }} />
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{s.label}</div>
                <ul className="text-[11px] mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {[
                    ["8+ characters", s.checks[0]],
                    ["Uppercase letter", s.checks[1]],
                    ["Lowercase letter", s.checks[2]],
                    ["Number", s.checks[3]],
                    ["Special character", s.checks[4]],
                  ].map(([lbl, ok]) => (
                    <li key={lbl} className={ok ? "text-emerald-700" : "text-slate-400"}>
                      {ok ? "✓" : "·"} {lbl}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Confirm Password</label>
              <input data-testid="fp-confirm-password" type="password" value={cp}
                     onChange={(e) => setCp(e.target.value)}
                     className="w-full border border-slate-200 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              {cp && np !== cp && (
                <div className="text-[11px] text-red-600 mt-1">Passwords don't match</div>
              )}
            </div>
            <button data-testid="fp-reset-btn" onClick={reset} disabled={busy || s.passed < 5 || np !== cp}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded disabled:bg-slate-300 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={14} />}
              {busy ? "Saving…" : "Update Password"}
            </button>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-6" data-testid="fp-done">
            <CheckCircle2 size={48} className="text-emerald-600 mx-auto mb-3" />
            <div className="font-editorial text-xl mb-1">All done</div>
            <p className="text-sm text-slate-600">
              Your password has been reset successfully.<br />
              Please log in with your new password.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
